'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  nextParallelAgentBatchMemberIds,
  sameMemberIds,
  summarizeParallelAgentBatch,
  type ParallelAgentBatchSummary,
} from './parallel-agent-batch';
import type { ParallelAgentExecution } from './parallel-agent-execution';

export const PARALLEL_BATCH_FINISHED_VISIBLE_MS = 1200;
export const PARALLEL_BATCH_FADE_OUT_MS = 400;

export type ParallelAgentBatchState = {
  batchId: string | undefined;
  members: ParallelAgentExecution[];
  summary: ParallelAgentBatchSummary;
  isVisible: boolean;
  isLeaving: boolean;
};

export function useParallelAgentBatch({
  executions,
  expandedBatchId,
}: {
  executions: readonly ParallelAgentExecution[];
  expandedBatchId: string | undefined;
}): ParallelAgentBatchState {
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [leavingBatchId, setLeavingBatchId] = useState<string>();
  const [dismissedBatchId, setDismissedBatchId] = useState<string>();
  const nextMemberIds = nextParallelAgentBatchMemberIds({
    previousMemberIds: memberIds,
    executions,
  });

  if (!sameMemberIds(memberIds, nextMemberIds)) {
    setMemberIds(nextMemberIds);
  }

  const members = useMemo(() => {
    const byToolCallId = new Map(
      executions.map((execution) => [execution.toolCallId, execution]),
    );

    return nextMemberIds.flatMap((id) => {
      const execution = byToolCallId.get(id);

      return execution ? [execution] : [];
    });
  }, [executions, nextMemberIds]);
  const summary = summarizeParallelAgentBatch(members);
  const batchId = nextMemberIds[0];
  const shouldAutoDismiss =
    !!batchId && summary.isFinished && expandedBatchId !== batchId;

  useEffect(() => {
    if (!shouldAutoDismiss) {
      return;
    }

    const leaveTimer = setTimeout(
      () => setLeavingBatchId(batchId),
      PARALLEL_BATCH_FINISHED_VISIBLE_MS,
    );
    const dismissTimer = setTimeout(
      () => setDismissedBatchId(batchId),
      PARALLEL_BATCH_FINISHED_VISIBLE_MS + PARALLEL_BATCH_FADE_OUT_MS,
    );

    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(dismissTimer);
      setLeavingBatchId(undefined);
    };
  }, [batchId, shouldAutoDismiss]);

  return {
    batchId,
    members,
    summary,
    isVisible:
      !!batchId && (dismissedBatchId !== batchId || !summary.isFinished),
    isLeaving: shouldAutoDismiss && leavingBatchId === batchId,
  };
}
